import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useAuthStore } from '@/stores/use-auth-store';
import { creditsApi } from './api';
import type {
  AgingReport,
  ApplyPaymentInput,
  CreateAccountInput,
  CreditAccount,
  CreditAccountListParams,
  CreditAccountListResult,
  CreditInvoice,
  CreditInvoiceListParams,
  CreditInvoiceListResult,
  LedgerParams,
  LedgerResult,
  UpdateAccountInput,
} from './types';

function useTenantId(): string {
  return useAuthStore((s) => s.profile?.tenant_id ?? 'anonymous');
}

export function useCreditAccounts(params: CreditAccountListParams): UseQueryResult<CreditAccountListResult> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['credits', tenantId, 'accounts', params.page ?? 1, params.search ?? '', params.status ?? ''],
    queryFn: () => creditsApi.accounts(params),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useCreditAccount(id: string | undefined): UseQueryResult<CreditAccount | null> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['credits', tenantId, 'account', id],
    queryFn: () => (id ? creditsApi.account(id) : Promise.resolve(null)),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateCreditAccount() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAccountInput) => creditsApi.createAccount(input),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['credits', tenantId, 'accounts'] }); },
  });
}

export function useUpdateCreditAccount() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateAccountInput }) => creditsApi.updateAccount(id, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['credits', tenantId, 'accounts'] });
      qc.invalidateQueries({ queryKey: ['credits', tenantId, 'account', vars.id] });
    },
  });
}

export function useCreditAccountLedger(id: string | undefined, params: LedgerParams): UseQueryResult<LedgerResult> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['credits', tenantId, 'ledger', id, params.page ?? 1, params.start_date ?? '', params.end_date ?? ''],
    queryFn: () => creditsApi.ledger(id!, params),
    enabled: Boolean(id),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useCreditAccountAging(id: string | undefined): UseQueryResult<AgingReport | null> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['credits', tenantId, 'aging', id],
    queryFn: () => (id ? creditsApi.aging(id) : Promise.resolve(null)),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreditInvoices(params: CreditInvoiceListParams): UseQueryResult<CreditInvoiceListResult> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['credits', tenantId, 'invoices', params.page ?? 1, params.account_id ?? '', params.status ?? '', params.location_id ?? '', params.date_from ?? '', params.date_to ?? '', params.customer_search ?? ''],
    queryFn: () => creditsApi.invoices(params),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useCreditInvoice(id: string | undefined): UseQueryResult<CreditInvoice | null> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['credits', tenantId, 'invoice', id],
    queryFn: () => (id ? creditsApi.invoice(id) : Promise.resolve(null)),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useApplyPayment() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, input }: { invoiceId: string; input: ApplyPaymentInput }) =>
      creditsApi.applyPayment(invoiceId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['credits', tenantId, 'invoices'] });
      qc.invalidateQueries({ queryKey: ['credits', tenantId, 'accounts'] });
      qc.invalidateQueries({ queryKey: ['credits', tenantId, 'ledger'] });
      qc.invalidateQueries({ queryKey: ['credits', tenantId, 'aging'] });
    },
  });
}
